import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, MapPin } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { API } from "../lib/utils";

const PricingPage = () => {
  const { t, lang } = useLang();
  const [packages, setPackages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/packages`).then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const groups = {
    natation: packages.filter((p) => p.discipline === "natation"),
    natation_adulte: packages.filter((p) => p.discipline === "natation_adulte"),
    cyclisme: packages.filter((p) => p.discipline === "cyclisme"),
  };

  const renderTile = (pkg) => {
    const featured = pkg.featured;
    const sessions = pkg.sessions || 1;
    const desc = lang === "fr" ? pkg.description_fr : pkg.description_en;
    return (
      <article
        key={pkg.id}
        data-testid={`pricing-tile-${pkg.id}`}
        className={`relative flex flex-col p-10 border ${
          featured ? "border-burnt border-2 bg-bone" : "border-border bg-white"
        } hover:-translate-y-1 transition-transform duration-300`}
      >
        {featured && (
          <span className="absolute -top-3 left-10 bg-burnt text-white font-heading uppercase tracking-widest text-xs font-bold px-3 py-1">
            {t("pricing.popular")}
          </span>
        )}
        <h3 className="hd-italic text-3xl text-forest leading-tight">
          {lang === "fr" ? pkg.name_fr : pkg.name_en}
        </h3>
        {(pkg.duration_fr || pkg.duration_en) && (
          <span
            data-testid={`duration-${pkg.id}`}
            className="mt-3 inline-flex w-fit items-center bg-forest/10 text-forest font-heading uppercase tracking-widest text-xs font-bold px-2.5 py-1"
          >
            {lang === "fr" ? pkg.duration_fr : pkg.duration_en}
          </span>
        )}
        {desc && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{desc}</p>}
        <div className="mt-6 flex items-baseline gap-2">
          <span className="font-heading font-black text-6xl text-forest">{pkg.amount}$</span>
          <span className="text-sm text-muted-foreground uppercase tracking-widest">CAD</span>
        </div>
        {sessions > 1 && (
          <p className="mt-2 text-sm text-muted-foreground">
            {(pkg.amount / sessions).toFixed(0)}$ {t("pricing.per_session")}
          </p>
        )}
        <ul className="mt-8 space-y-3 flex-1">
          <li className="flex items-start gap-3 text-sm">
            <Check size={16} className="text-burnt mt-0.5 shrink-0" />
            <span>{lang === "fr" ? "Coaching personnalisé" : "Custom coaching"}</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <Check size={16} className="text-burnt mt-0.5 shrink-0" />
            <span>{lang === "fr" ? "Tous les âges et niveaux" : "All ages and levels"}</span>
          </li>
          <li className="flex items-start gap-3 text-sm">
            <Check size={16} className="text-burnt mt-0.5 shrink-0" />
            <span>{lang === "fr" ? "Entraîneurs certifiés" : "Certified coaches"}</span>
          </li>
          {sessions > 1 && (
            <li className="flex items-start gap-3 text-sm">
              <Check size={16} className="text-burnt mt-0.5 shrink-0" />
              <span>{lang === "fr" ? "Suivi hebdomadaire" : "Weekly follow-up"}</span>
            </li>
          )}
        </ul>
        <button
          data-testid={`book-${pkg.id}`}
          onClick={() => navigate(`/reservation?package=${pkg.id}`)}
          className={featured ? "btn-primary mt-10 w-full" : "btn-secondary mt-10 w-full"}
        >
          {t("pricing.book_now")} <ArrowRight size={16} />
        </button>
      </article>
    );
  };

  return (
    <div data-testid="page-pricing" className="bg-white">
      <section className="container-flow section-pad">
        <p className="text-overline">{t("pricing.overline")}</p>
        <h1 className="mt-4 hd-italic text-5xl md:text-7xl text-forest leading-[0.95] max-w-4xl">{t("pricing.title")}</h1>
        <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">{t("pricing.subtitle")}</p>

        <div className="mt-8 inline-flex items-center gap-3 bg-bone border border-border px-5 py-3" data-testid="travel-notice">
          <MapPin size={18} className="text-burnt" />
          <p className="text-sm text-forest font-medium">{t("pricing.travel_notice")}</p>
        </div>

        {/* Swimming — 30 min */}
        {groups.natation.length > 0 && (
          <div className="mt-16" data-testid="section-natation">
            <div className="mb-8">
              <h2 className="hd-italic text-3xl md:text-4xl text-forest">{t("pricing.swim_section")}</h2>
              <p className="mt-2 text-sm font-heading uppercase tracking-widest text-burnt">{t("pricing.swim_section_sub")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {groups.natation.map(renderTile)}
            </div>
          </div>
        )}

        {/* Adult swimming — 1h */}
        {groups.natation_adulte.length > 0 && (
          <div className="mt-16" data-testid="section-natation-adulte">
            <div className="mb-8">
              <h2 className="hd-italic text-3xl md:text-4xl text-forest">{t("pricing.swim_adult_section")}</h2>
              <p className="mt-2 text-sm font-heading uppercase tracking-widest text-burnt">{t("pricing.swim_adult_section_sub")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {groups.natation_adulte.map(renderTile)}
            </div>
          </div>
        )}

        {/* Cycling — virtual follow-up */}
        {groups.cyclisme.length > 0 && (
          <div className="mt-16" data-testid="section-cyclisme">
            <div className="mb-8">
              <h2 className="hd-italic text-3xl md:text-4xl text-forest">{t("pricing.cycle_section")}</h2>
              <p className="mt-2 text-sm font-heading uppercase tracking-widest text-burnt">{t("pricing.cycle_section_sub")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {groups.cyclisme.map(renderTile)}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default PricingPage;
